import { Comment, Fragment, Text, h, isVNode, ref, type FunctionalComponent, type VNode, type VNodeArrayChildren } from 'vue';

/**
 * The tooltip half of "a pill never gets cut, its text ends in an ellipsis":
 * when a label is actually truncated, the pill's full text becomes the native
 * `title`, set on hover (a resize can change what fits, so it is measured at
 * the moment it matters, not once on mount). A label that fits gets no title.
 * `textClass` is the class `wrapTextRuns` gives the ellipsising text spans.
 */
export function useTruncationTitle(textClass: string) {
  const rootEl = ref<HTMLElement | null>(null);
  const title = ref<string | undefined>(undefined);

  function reveal() {
    const root = rootEl.value;
    if (!root) return;
    const truncated = Array.from(root.querySelectorAll<HTMLElement>(`.${textClass}`)).some(
      (el) => el.scrollWidth > el.clientWidth,
    );
    title.value = truncated ? (root.textContent ?? '').replace(/\s+/g, ' ').trim() || undefined : undefined;
  }

  return { rootEl, title, reveal };
}

/**
 * Wraps only the TEXT of a pill's slot in an ellipsising span: each run of
 * adjacent text becomes one `<span class="{className}">`, while element children
 * (a status dot, an icon, a caller's own `<span>`) stay direct flex items of the
 * pill — so they keep their size, the pill's gap and its vertical centring.
 */
export function wrapTextRuns(nodes: VNodeArrayChildren, className: string): VNode[] {
  const out: VNode[] = [];
  let run: string[] = [];

  const flush = () => {
    const text = run.join('');
    run = [];
    if (text.trim()) out.push(h('span', { class: className }, text));
  };

  const visit = (children: VNodeArrayChildren) => {
    for (const child of children) {
      if (child == null || typeof child === 'boolean') continue;
      if (typeof child === 'string' || typeof child === 'number') {
        run.push(String(child));
      } else if (Array.isArray(child)) {
        visit(child);
      } else if (isVNode(child) && child.type === Text) {
        run.push(String(child.children ?? ''));
      } else if (isVNode(child) && child.type === Fragment && Array.isArray(child.children)) {
        visit(child.children as VNodeArrayChildren);
      } else if (isVNode(child) && child.type === Comment) {
        out.push(child);
      } else if (isVNode(child)) {
        flush();
        out.push(child);
      }
    }
  };

  visit(nodes);
  flush();
  return out;
}

/**
 * Renders the vnodes it is GIVEN. The pill calls its slot in its own render and
 * passes the wrapped result in as a prop: a fresh array every render, so this
 * child re-renders whenever the pill does. (A prop-less child calling the slot
 * itself was skipped on the pill's re-render and kept a replaced row's old text.)
 */
export const RenderNodes: FunctionalComponent<{ nodes: VNode[] }> = (props) => props.nodes;
RenderNodes.props = ['nodes'];
